import React from 'react'
import DOMPurify from 'dompurify'
import { Link } from 'react-router-dom'
import { Tooltip, IconButton, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/pro-duotone-svg-icons/faQuestionCircle'

export const getTranslationFromItem = (item: any, kedo: any) => {
    if (!item.translations || item.translations.length <= 0) {
        return
    }
    const locale = kedo.env().getSelectedLocale()
    let trans = item.translations.find(
        (trans: any) => trans.culture === locale.code
    )
    if (!trans && item.translations.length > 0) {
        trans = item.translations[0]
    }
    return trans
}

export const replaceSpecialTags = (preText: string) => {
    if (preText.indexOf('{') === -1) {
        return preText
    }
    const parts = preText.split(/\{dossier:([0-9]+)\}/g)
    const elements: any[] = []
    for (let i = 0; i < parts.length; i++) {
        if (i % 2 === 1) {
            elements.push(
                <Link key={i} target="_new" to={`/contentdossier/${parts[i]}`}>
                    {parts[i]}
                </Link>
            )
        } else {
            elements.push(parts[i])
        }
    }
    return <div>{elements}</div>
}

export const isObligatory = (
    item: any,
    conditions: any,
    getValues: () => any
) => {
    if (
        item.def_dossier_def_field &&
        item.def_dossier_def_field.obligatory &&
        item.def_dossier_def_field.obligatory === true
    ) {
        return true
    }
    if (item.def_dossier_link && item.def_dossier_link.obligatory === true) {
        return true
    }
    if (conditions) {
        const values = getValues()
        let condResult = false
        conditions.forEach((condition: any) => {
            if (conditionMatches(condition, values)) {
                if (
                    condition.actions
                        .filter(
                            (action: any) => action.action === 'require_fields'
                        )
                        .filter((action: any) =>
                            action.values.find(
                                (actVal: any) => actVal === item.id
                            )
                        ).length > 0
                ) {
                    condResult = true
                }
            }
        })
        return condResult
    }
    return false
}

export const isEditMode = (mode: string) => {
    if (mode === 'edit' || mode === 'show/edit') {
        return true
    }
    return false
}

export const getMinimalColspan = (item: any) => {
    let minColSpan = 12
    const width = window.innerWidth
    if (width < 576) {
        minColSpan = 12
    } else if (width > 576 && width < 768 && item.colspan < 6) {
        minColSpan = 6
    } else if (width > 768 && width < 992 && item.colspan < 4) {
        minColSpan = 4
    } else if (width > 992 && width < 1200 && item.colspan < 2) {
        minColSpan = 2
    } else if (width > 1200) {
        minColSpan = 1
    }
    if (item.colspan < minColSpan) {
        return minColSpan
    }
    return item.colspan
}

export const conditionRuleFieldMatches = (fieldRule: any, values: any) => {
    const value = values[fieldRule.field]
    let result = false
    if (fieldRule.field_method === 'equals') {
        fieldRule.values.forEach((fieldRuleValId: any) => {
            if (
                value &&
                value.find &&
                value.find(
                    (listOptionId: any) => listOptionId === fieldRuleValId
                )
            ) {
                result = true
            }
        })
    } else if (fieldRule.field_method === 'notequals') {
        fieldRule.values.forEach((fieldRuleValId: any) => {
            if (
                (value &&
                    value.find &&
                    value.find(
                        (listOptionId: any) => listOptionId !== fieldRuleValId
                    )) ||
                (value && value.length <= 0)
            ) {
                result = true
            }
        })
    }
    return result
}

export const conditionMatches = (condition: any, values: any) => {
    const result = false
    const conditionRuleMatches = condition.rules.filter((rule: any) => {
        const wantLength = rule.type === 'or' ? 1 : rule.fields.length
        return (
            rule.fields.filter((ruleField: any) =>
                conditionRuleFieldMatches(ruleField, values)
            ).length >= wantLength
        )
    })
    if (conditionRuleMatches.length > 0) {
        return true
    }
    return result
}

export const checkCondition = (
    conditions: any,
    hiddenfields: any,
    item: any,
    condAct = 'show_fields',
    getValues?: () => any
) => {
    if (!conditions || !hiddenfields || conditions.length <= 0) {
        return true
    }
    let result = !hiddenfields.find((hiddenId: any) => hiddenId === item.id)
    if (result) {
        return true
    }
    // Only check conditions with values if getValues is provided
    if (!getValues) {
        return result
    }
    const values = getValues()
    conditions.forEach((condition: any) => {
        if (conditionMatches(condition, values)) {
            if (
                condition.actions
                    .filter((action: any) => action.action === condAct)
                    .filter((action: any) =>
                        action.values.find((actVal: any) => actVal === item.id)
                    ).length > 0
            ) {
                result = true
            }
        }
    })
    return result
}

export const shouldListenForConditions = (conditions: any, item: any) => {
    if (!conditions || conditions.length <= 0) {
        return false
    }
    let result = false
    conditions.forEach((condition: any) => {
        condition.rules.forEach((rule: any) => {
            if (rule.fields.find((field: any) => field.field === item.id)) {
                result = true
            }
        })
    })
    return result
}

export const isView = (currentView: string, itemView: string) => {
    if (
        currentView === 'show' &&
        (itemView === 'show' || itemView === 'show/edit')
    ) {
        return true
    }
    if (
        currentView === 'edit' &&
        (itemView === 'edit' || itemView === 'show/edit')
    ) {
        return true
    }
    return currentView === itemView
}

export const returnContent = (displayItem: any, content: any, mode: string) => {
    if (!content) return null
    if (mode === 'search' || mode === 'advanced-search') {
        const contentObject: { [key: string]: any } = {}
        content.forEach((item: any) => (contentObject[item.id] = item.value))
        return contentObject[displayItem.id]
    } else {
        return content[displayItem.id]
    }
}

export const renderInformationText = (
    translation: any,
    show_info_direct: boolean
) => {
    if (!translation?.settings?.informationtext) return null

    const sanitizedHtml = DOMPurify.sanitize(
        translation.settings.informationtext,
        {
            ALLOWED_TAGS: [
                'b',
                'img',
                'br',
                'span',
                'table',
                'ol',
                'tbody',
                'li',
                'i',
                'strong',
                'tr',
                'td',
                'thead',
                'div',
                'p',
            ],
            ALLOWED_ATTR: ['style', 'border', 'src', 'width', 'height'],
        }
    )

    if (show_info_direct) {
        return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    }

    // Conditional tooltip content
    const isMultiLine = translation.settings.informationtext.includes('\n')
    const tooltipContent = (
        <Box
            sx={{
                maxWidth: 300,
                maxHeight: isMultiLine ? 200 : 'auto',
                overflowY: isMultiLine ? 'auto' : 'visible',
                p: 1,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
    )

    return (
        <Tooltip title={tooltipContent} placement="top" arrow>
            <IconButton size="small" sx={{ py: 0 }}>
                <FontAwesomeIcon icon={faQuestionCircle} />
            </IconButton>
        </Tooltip>
    )
}
